import {ApiService, mithra_http} from "./Http";
import type {AxiosInstance, AxiosResponse} from "axios";
import type {
    ApplyTaxonomyMappingRequestData,
    BusinessUnit,
    MatCategoryConcentrationStatistics,
    MatConcentrationStatistics,
    MatReviewLevelStatisticsTreeSerializer,
    MatReviewSampleStatistic,
    MatReviewStatisticsSerializer,
    MatSupplierCategoryConcentrationStatistics,
    PartData,
    PpvGroup,
    PpvLevelStatisticsTreeSerializer,
    PpvPart,
    ReviewChoice,
} from "./classes/MaterializedClasses";
import type {ApiSuggestionTreeResponse, ApiUpdateSuggestionTreeResponse, PagePromise} from "./ApiHelpers";
import {
    setLevelFilterParams,
    setLevelFilterUrlParams,
    setParamsOrNull,
    setSearchUrlParams,
    transformAdvancedDashboardTableResponse
} from "./ApiHelpers";
import type {Bag, Revision} from "./classes/Bag";
import type {PartHistoryEntry} from "./classes/PartHistory";
import {environment} from "../env";
import {m_taxonomy} from "./classes/TaxonomyClasses";
import type {
    ApprovalPart,
    ApprovalRequest,
    ApprovalRequestFilters,
    ApprovalStatusCount,
    ApprovalStatusEnum,
    CategorizationApprovalRequest,
    CategorizationApprovalStats,
    CreateCategorizationApprovalStats,
    DevopsCreateCategorizationApprovalStats,
    RunningJobStatusResponse,
    TaxonomyApprovalRequest,
    TaxonomyCategoryResp,
    TaxonomyCategoryRespV2
} from "./classes/AiClasses";
import type {
    AdvancedBusinessUnitPivotData,
    AdvancedDashboardPageResponse,
    AdvancedGeoData,
    AdvancedOpportunityData,
    AdvancedSpendData,
    AdvancedSpendKPI,
    AdvancedSpendPerGroup,
    AdvancedSupplierBreakdownData,
    AdvancedTreeData,
    AiSupplierParentIdentificationJobStatusSerializer,
    CategorizationVersionedStatisticsSerializer,
    DonutChartSupplierSpendData,
    ExcelDataColumns,
    GoldenRecordCluster,
    GoldenRecordClusterPropertyType,
    GraphResponse,
    HierarchyMultiValueResponse,
    HttpQueryParams,
    InsightsJobRunning,
    MatCommonKpiSerializer,
    MatKpiTreeData,
    MatKpiTreeValues,
    PageResponse,
    TaxonomyHealthCheckSerializer,
    TaxonomySuggestionSerializer,
    TotalSpend
} from "./ApiTypes";
import {SupplierSegmentationEnum} from "./ApiTypes";
import type {
    DownloadTableRequest,
    MatSupplierFilter,
    MatSupplierFilter_V2,
    StorePartFeedbackManySerializer,
    StorePartFeedbackSerializer,
} from "./classes/MatReviewClasses";
import type {
    CommonParentSupplier,
    GeocodedRegion,
    MatSupplierReviewStatistics,
    ParentSupplier,
    ParentSupplierChoice,
    ParentSupplierReviewRow,
    Supplier,
    SupplierReviewRow,
    UpdateParentSupplierReviewRowData,
    UpdateSupplierData,
} from "../pages/supplier-normalization/pages/SupplierNormalization.classes";
import {makeObservable, observable, toJS} from "mobx";
import type {taxonomy_health_check} from "./classes/TaxonomyHealthCheckClasses";
import type {taxonomy_suggester_v2} from "./classes/TaxonomySuggesterClassesV2";
import type {
    PpvAllLevelsFilter,
    PpvBusinessUnitSearchResponse,
    PpvCcDescriptionSearchResponse,
    PpvFilter,
    PpvGroupFilter,
    PpvItemDeletionFilter,
    PpvItemListFilter,
    PpvPNameSearchResponse,
    PpvSupplierSearchResponse
} from "../jobs/koi/purchase-price-variance/classes/PpvApiClasses";
import type {Nullable, OnlyRequired} from "../utils/ts-utils";
import type {
    CreatePpvOpportunity,
    PpvOpportunity,
    PpvOpportunity_PATCH,
    PpvOpportunityListFilter,
    PpvStatsPerPart,
    PpvStatsPerQuarter,
    PpvStatsPerSupplier
} from "./classes/OpportunityClasses";
import type {AiCategorizationJobCreateSerializer} from "./classes/JobClasses";
import type {CategorizationStatus} from "./classes/CategorizationClasses";
import type {
    CreateTaxonomyApprovalOperationSerializer,
    TaxonomyApprovalRequestStateSerializer
} from "./classes/TaxonomyApproval";
import type {MessageElement} from "../pages/chat/ChatTypes";
import {toParams} from "../utils/js-utils";
import {PPV_FILTERS_CACHED} from "../jobs/koi/purchase-price-variance/classes/PpvConfig";
import type {CustomerConfiguration, DatabagConfiguration} from "../stores/config/DatabagConfigClasses";
import SetDatabagForTaxonomy = m_taxonomy.SetDatabagForTaxonomy;

export type Ordering = {field: string, desc: boolean};

export class MithraMaterializedApi extends ApiService {

    revisionHack: undefined | 'customer' | string = undefined;

    constructor(public http: AxiosInstance) {
        super(http);
        makeObservable(this, {revisionHack: observable})
    }

    static buildSGroupUrlSearchParams(filter: MatSupplierFilter): URLSearchParams {
        const params = new URLSearchParams();
        params.set('databag', String(filter.databag));
        params.set('group_by', 'supplier_id');
        setParamsOrNull(params, {'business_unit_id': filter.business_unit});
        setSearchUrlParams(params, filter.search);
        setLevelFilterUrlParams(params, filter, 'p_l');
        if (filter.approval) {
            params.set('approval', String(filter.approval));
        }
        return params
    }

    getServerPing() {
        return this.http.get<boolean>('/ping/');
    }

    storePartFeedback(partFeedbackId: number, data: StorePartFeedbackSerializer): Promise<AxiosResponse<unknown>> {
        return this.http.patch(`/m_review/base/approval/${partFeedbackId}/store_feedback/`, data)
    }

    storePartFeedbackMany(data: StorePartFeedbackManySerializer): Promise<AxiosResponse<unknown>> {
        return this.http.patch(`/m_review/base/approval/store_feedback_many/`, data)
    }

    storePartFeedbackAll(approval: number, feedback_choice: ReviewChoice): Promise<AxiosResponse<unknown>> {
        return this.http.patch(`/m_review/base/approval/store_feedback_all/`, {approval, feedback_choice})
    }

    listReviewStatistics(bagId: number): Promise<AxiosResponse<MatReviewStatisticsSerializer[]>> {
        const params = new URLSearchParams();
        params.set('databag', String(bagId));

        this.applyRevisionHack(params);

        return this.http.get(`/m_review/stats/total/`, {params})
    }

    listPpvLevelStatistics(filter: PpvAllLevelsFilter): Promise<AxiosResponse<[PpvLevelStatisticsTreeSerializer]>> {
        // Ensure that the PpvFilter does not contain level filters
        return this.http.get(`/m_koi/ppv_statistics/as_tree/`, {params: filter, paramsSerializer: {indexes: null}})
    }

    listPpvSearchSuppliers(search: string, filter: Partial<PpvFilter>): PagePromise<PpvSupplierSearchResponse> {
        const params: Partial<PpvFilter> & {search: string} = {...filter, search: search}
        delete params.supplier__in;
        delete params.supplier__not;
        return this.http.get(`/m_koi/ppv_filter/supplier/` + (PPV_FILTERS_CACHED ? 'cached/' : ''), {
            params,
            paramsSerializer: {indexes: null}
        })
    }

    listPpvSearchBusinessUnits(search: string, filter: Partial<PpvFilter>): PagePromise<PpvBusinessUnitSearchResponse> {
        const params: Partial<PpvFilter> & {search: string} = {...filter, search: search}
        delete params.business_unit;
        delete params.business_unit__in;
        delete params.business_unit__not;
        return this.http.get(`/m_koi/ppv_filter/business_unit/` + (PPV_FILTERS_CACHED ? 'cached/' : ''), {
            params,
            paramsSerializer: {indexes: null}
        })
    }

    listPpvSearchCCDescription(search: string, filter: Partial<PpvFilter>): PagePromise<PpvCcDescriptionSearchResponse> {
        const params: Partial<PpvFilter> & {search: string} = {...filter, search: search}
        delete params.cc_description__in;
        delete params.cc_description__not;
        return this.http.get(`/m_koi/ppv_filter/cc_description/` + (PPV_FILTERS_CACHED ? 'cached/' : ''), {
            params,
            paramsSerializer: {indexes: null}
        })
    }

    listPpvSearchPName(search: string, filter: Partial<PpvFilter>): PagePromise<PpvPNameSearchResponse> {
        const params: Partial<PpvFilter> & {search: string} = {...filter, search: search}
        delete params.p_name__in;
        delete params.p_name__not;
        return this.http.get(`/m_koi/ppv_filter/p_name/` + (PPV_FILTERS_CACHED ? 'cached/' : ''), {
            params,
            paramsSerializer: {indexes: null}
        })
    }

    getPpvTotalExpectedSavings(databag: number): Promise<AxiosResponse<{total_expected_savings: number}>> {
        return this.http.get(`/m_koi/ppv_opportunity/total_expected_savings/`, {params: databag})
    }


    /**
     *
     * @param bagId
     * @param businessUnitId undefined: All BU, null: No BU set, number: Specific BU)
     * @param taxonomySize
     */
    listReviewLevelStatistics(bagId: number, businessUnitId: undefined | null | number, taxonomySize: number): Promise<AxiosResponse<[MatReviewLevelStatisticsTreeSerializer]>> {
        if (businessUnitId && Number.isInteger(businessUnitId) && businessUnitId < 0) {
            throw new Error(`Unexpected business unit id ${businessUnitId}`)
        }
        const params = new URLSearchParams();
        params.set('all_business_units', String(businessUnitId === undefined));
        params.set('databag', String(bagId));
        params.set('level__lte', String(taxonomySize));
        setParamsOrNull(params, {'business_unit': businessUnitId})

        this.applyRevisionHack(params);

        return this.http.get(`/m_review/stats/level/as_tree/`, {params})
    }

    getBag(id: number): Promise<AxiosResponse<Bag>> {
        return this.http.get<Bag>(`/bag/${id}/`);
    }

    getAllBags(): Promise<Bag[]> {
        const params = {ordering: '-timestamp'}
        return this.http.get<Bag[]>(`/bag/`, {params}).then(r => r.data);
    }

    getAllRevisions(): Promise<AxiosResponse<Revision[]>> {
        return this.http.get('/revision/');
    }

    // Approval

    createTaxonomyApprovalRequest(taxonomy: number, notes: string): Promise<AxiosResponse<TaxonomyApprovalRequest>> {
        return this.http.post<TaxonomyApprovalRequest>(`/approve/taxonomy/`, {taxonomy, notes})
    }

    createCategorizationApprovalRequestForAll(notes: string, databag: number): Promise<AxiosResponse<CategorizationApprovalRequest>> {
        return this.http.post<CategorizationApprovalRequest>(`/approve/category/create_for_all/`, {
            databag,
            notes
        })
    }

    getEgoCategorizationCreateApprovalStatistics(databag: number) {
        const params = {databag}
        return this.http.get<CreateCategorizationApprovalStats>(`/approve/category/create_for_all_stats/`, {params})
    }

    devopsCreateCategorizationApprovalRequestForEveryone(notes: string, databag: number): Promise<AxiosResponse<CategorizationApprovalRequest>> {
        return this.http.post<CategorizationApprovalRequest>(`/approve/category/devops_create_for_everyone/`, {
            databag,
            notes
        })
    }

    devopsCreateCategorizationApprovalRequestForEveryoneStatistics(databag: number) {
        const params = {databag}
        return this.http.get<DevopsCreateCategorizationApprovalStats>(`/approve/category/devops_create_for_everyone_stats/`, {params})
    }

    getRunningJobs() {
        // TODO CAT-2549: Consolidate polling-endpoints and migrate to PubSub topic
        return this.http.get<RunningJobStatusResponse>(`/system/running_jobs/`)
    }

    approvalStatusCount(): Promise<AxiosResponse<ApprovalStatusCount[]>> {
        return this.http.get<ApprovalStatusCount[]>('/approval/v2/group_by_status_count/')
    }

    listApprovalRequestsV2(page: number, page_size: number, filters: ApprovalRequestFilters): Promise<AxiosResponse<PageResponse<ApprovalRequest>>> {
        const params = {page, page_size, ...filters};
        return this.http.get('/approval/v2/', {params});
    }

    approvalItem(id: number): Promise<AxiosResponse<ApprovalRequest>> {
        return this.http.get('/approval/v2/' + id.toString() + '/');
    }

    approvalCategorizationPartList(id: number, filters: string[][]): Promise<AxiosResponse<PageResponse<ApprovalPart>>> {
        const params = new URLSearchParams(filters);
        return this.http.get('/approval/v2/' + id.toString() + '/categorization/', {params});
    }

    listApprovalRequests(): Promise<AxiosResponse<ApprovalRequest[]>> {
        // TODO: This should be phased out!
        return this.http.get<ApprovalRequest[]>('/approve/')
    }

    storeApprovalNotes(approvalId: number, feedback_notes: string): Promise<AxiosResponse<ApprovalRequest[]>> {
        return this.http.patch<ApprovalRequest[]>(`/approve/${approvalId}/`, {feedback_notes})
    }

    overrideApprovalStatus(approvalId: number, status: ApprovalStatusEnum) {
        return this.http.patch<ApprovalRequest[]>(`/approve/${approvalId}/override/`, {status})
    }

    deleteApproval(approvalId: number) {
        return this.http.delete(`/approve/${approvalId}/`)
    }

    getTaxonomyApprovalRequest(approvalId: number): Promise<AxiosResponse<TaxonomyApprovalRequest>> {
        return this.http.get<TaxonomyApprovalRequest>(`/approve/taxonomy/${approvalId}/`)
    }

    applyTaxonomyApproval(approvalId: number, status: ApprovalStatusEnum, feedback_notes: string): Promise<AxiosResponse<TaxonomyApprovalRequest>> {
        console.warn('applyTaxonomyApproval is deprecated, use applyTaxonomyApprovalRequest instead')
        return this.http.post<TaxonomyApprovalRequest>(`/approve/taxonomy/${approvalId}/apply/`, {
            status,
            feedback_notes,
        })
    }

    applyTaxonomyOnlyApprovalRequest(approvalId: number, feedback_notes: string): Promise<AxiosResponse<TaxonomyApprovalRequest>> {
        return this.http.post<TaxonomyApprovalRequest>(`/approve/taxonomy/${approvalId}/apply_taxonomy_only/`, {feedback_notes})
    }

    getTaxonomyApprovalState(approvalId: number): Promise<AxiosResponse<TaxonomyApprovalRequestStateSerializer>> {
        return this.http.get(`/approve/taxonomy/${approvalId}/state/`)
    }

    updateTaxonomyApprovalState(approvalId: number, data: CreateTaxonomyApprovalOperationSerializer): Promise<AxiosResponse<TaxonomyApprovalRequestStateSerializer>> {
        return this.http.put(`/approve/taxonomy/${approvalId}/state/next/`, data)
    }

    gotoTaxonomyApprovalHistory(approvalId: number, history_number: number): Promise<AxiosResponse<TaxonomyApprovalRequestStateSerializer>> {
        return this.http.put(`/approve/taxonomy/${approvalId}/state/history/`, {history_number})
    }

    getCategorizationApprovalRequest(approvalId: number): Promise<AxiosResponse<CategorizationApprovalRequest>> {
        return this.http.get<CategorizationApprovalRequest>(`/approve/category/${approvalId}/`)
    }

    getCategorizationApprovalStatsRequest(approvalId: number): Promise<AxiosResponse<CategorizationApprovalStats>> {
        return this.http.get(`/approve/category/${approvalId}/stats/`)
    }

    applyCategorizationApproval(approvalId: number, status: ApprovalStatusEnum): Promise<AxiosResponse<never>> {
        // Do we want to add feedback_notes here as well?
        return this.http.post(`/approve/category/${approvalId}/apply/`, {status})
    }

    // Taxonomy

    getTaxonomyCategories(taxonomyId: number): Promise<AxiosResponse<TaxonomyCategoryResp>> {
        // Extracted directly from the dataset
        return this.http.get(`/taxonomy/${taxonomyId}/category_list/`)
    }

    getTaxonomyCategoriesV2(taxonomyId: number): Promise<AxiosResponse<TaxonomyCategoryRespV2>> {
        // Extracted directly from the dataset
        return this.http.get(`/taxonomy/${taxonomyId}/category_list_v2/`)
    }


    listMTaxonomy(): Promise<AxiosResponse<m_taxonomy.SimpleSerializer[]>> {
        return this.http.get(`/taxonomy/`)
    }

    setDatabagForTaxonomy(databagId: number, d: SetDatabagForTaxonomy): Promise<AxiosResponse<any>> {
        return this.http.patch(`/bag/${databagId}/`, d)
    }

    getTaxonomy(taxonomyId: number): Promise<AxiosResponse<m_taxonomy.FullSerializer>> {
        return this.http.get(`/taxonomy/${taxonomyId}/`)
    }

    updateTaxonomy(taxonomyId: number, data: Partial<m_taxonomy.UpdateSerializer>): Promise<AxiosResponse<m_taxonomy.UpdateSerializer>> {
        return this.http.patch(`/taxonomy/${taxonomyId}/`, data)
    }

    listMTaxonomyHistory(taxonomyId: number): Promise<AxiosResponse<m_taxonomy.SimpleTaxonomyOperationSerializer[]>> {
        return this.http.get(`/taxonomy/${taxonomyId}/history/`)
    }

    gotoMTaxonomyHistory(taxonomyId: number, d: m_taxonomy.GotoHistorySerializer): Promise<AxiosResponse<m_taxonomy.FullSerializer>> {
        return this.http.patch(`/taxonomy/${taxonomyId}/goto_history/`, d)
    }

    storeTaxonomyState(taxonomyId: number, d: m_taxonomy.CreateTaxonomyOperationSerializer): Promise<AxiosResponse<m_taxonomy.FullSerializer>> {
        // pre: operation_number == taxonomy.operation_number + 1
        // Note: This method is allowed to overwrite history
        return this.http.post(`/taxonomy/${taxonomyId}/store_state/`, d)
    }

    // KPI

    getCommonKpi(bagId: number): Promise<AxiosResponse<MatCommonKpiSerializer>> {
        return this.http.get(`/bag_kpi/${bagId}/common_kpi/`);
    }

    getTaxonomyKpiFullDepth(databag: number): Promise<AxiosResponse<HierarchyMultiValueResponse<MatKpiTreeData>>> {
        return this.http.get(`/m_kpi/tree/`, {params: {databag}});
    }

    getTaxonomyKpi(databag: number, taxonomySize: number): Promise<AxiosResponse<HierarchyMultiValueResponse<MatKpiTreeData>>> {
        return this.http.get(`/m_kpi/tree/`, {params: {databag, level__lte: taxonomySize}});
    }

    /**
     * Returns the sankey graph for a single value (spend/parts/etc)
     */
    getTaxonomyGraphKpi(databag: number, value: MatKpiTreeValues): Promise<AxiosResponse<GraphResponse>> {
        // Possibly add level__lte to limit tree size
        return this.http.get(`/m_kpi/tree/as_graph/`, {params: {databag, value}});
    }

    // KOI's

    listSupplierSegmentationResult(bagId: number): Promise<AxiosResponse<Array<{
        // PageResponse<{}>
        id: number
        s_id: string
        p_spend: number
        cum_p_spend_perc: number
        cum_s_n_parts_perc: number
        s_n_parts: number
        s_n_cats: number
        s_seg: SupplierSegmentationEnum
        databag: number
    }>>> {
        return this.http.get(`/m_koi/ss/`, {params: {databag: bagId, no_page: 1}}).then(r => {
            r.data.forEach(r => r.s_seg = SupplierSegmentationEnum[r.s_seg])
            return r;
        })
    }

    listSpendConcentration(bagId: number): Promise<MatConcentrationStatistics | null> {
        return this.http.get<MatConcentrationStatistics[]>(`/m_koi/sc/`, {params: {databag: bagId, no_page: 1}})
            .then(r => {
                r.data.forEach(r => {
                    r.total_spend = Number(r.total_spend)
                    r.top_n_spend = Number(r.top_n_spend)
                })
                return r.data[0] || null;
            })
    }

    listSpendConcentrationCategoryL1(bagId: number): Promise<AxiosResponse<MatCategoryConcentrationStatistics[]>> {
        return this.http.get<MatCategoryConcentrationStatistics[]>(`/m_koi/sc/category/`, {
            params: {
                databag: bagId,
                no_page: 1,
                level: 1,
            }
        }).then(r => {
            r.data.forEach(r => {
                r.total_spend = Number(r.total_spend)
                r.top_n_spend = Number(r.top_n_spend)
            })
            return r;
        })
    }

    listSpendConcentrationCategory(bagId: number, level: number): Promise<AxiosResponse<MatCategoryConcentrationStatistics[]>> {
        return this.http.get<MatCategoryConcentrationStatistics[]>(`/m_koi/sc/category/`, {
            params: {
                databag: bagId,
                no_page: 1,
                level__lte: level,
            }
        }).then(r => {
            r.data.forEach(r => {
                r.total_spend = Number(r.total_spend)
                r.top_n_spend = Number(r.top_n_spend)
            })
            return r;
        })
    }

    listPaginatedSpendConcentrationSupplier(
        filter: MatSupplierFilter_V2, page: number, page_size: number, ordering?: Ordering
    ): PagePromise<MatSupplierCategoryConcentrationStatistics> {
        let params = {
            databag: filter.databag,
            level: filter.filterLevel,
            page_size,
            page,
        };
        if (ordering) {
            // BE: By default it's on the spend given the filter, other options are: s_total_spend or s_total_parts
            params['ordering'] = (ordering.desc ? '-' : '') + ordering.field;
        }
        if (filter.search) {
            if (filter.search.supplier) {
                params['search'] = filter.search.supplier;
            } else {
                console.warn('Search not supported for', filter.search);
            }
        }
        const fixLevels = filter.fixLevels === undefined ? filter.filterLevel : filter.fixLevels;
        params = setLevelFilterParams(fixLevels, filter, params);
        return this.http.get<PageResponse<MatSupplierCategoryConcentrationStatistics>>(`/m_koi/sc/supplier/`, {params}).then(r => {
            r.data.results.forEach(r => {
                r.s_c_spend = Number(r.s_c_spend)
                r.s_total_spend = Number(r.s_total_spend)
            })
            return r;
        })
    }

    listPartData(bagId: number, supplierId: number, page: number, page_size: number): PagePromise<PartData> {
        return this.http.get<PageResponse<PartData>>(`/data/part/`, {
            params: {
                databag_id: bagId, // This is not referenced in backend as it's the source data
                supplier: supplierId,
                page,
                page_size,
            }
        })
            .then(r => {
                r.data.results.forEach(r => {
                    r.p_spend = Number(r.p_spend)
                })
                return r;
            })
    }

    downloadPartFile(partId: number) {
        return this.http.post(`/data/part/${partId}/file/`, undefined, {responseType: 'blob'})
    }

    getPartHistory(partId: number): Promise<AxiosResponse<PartHistoryEntry[]>> {
        return this.http.get<PartHistoryEntry[]>(`/part_history/`, {
            params: {part_id: partId, no_page: true, ordering: '-created_at'}
        })
    }

    listBusinessUnitData(bagId: number, ordering?: Ordering): Promise<AxiosResponse<BusinessUnit[]>> {
        const params = {
            databag_id: bagId,
            no_page: true,
        };
        if (ordering) params['ordering'] = (ordering.desc ? '-' : '') + ordering.field;
        return this.http.get(`/data/business_unit/`, {params})
    }

    listMatTaxonomy(src_taxonomy: number): Promise<AxiosResponse<m_taxonomy.MaterializedTaxonomy[]>> {
        if (environment.isDemo) {
            if (src_taxonomy === 400) return mithra_http.mockApi.get('sales_demo/suggestor/src_taxonomy_a.json')
            else return mithra_http.mockApi.get('sales_demo/suggestor/src_taxonomy_b.json')
        }
        const params = {src_taxonomy, hidden: false};
        return this.http.get<m_taxonomy.MaterializedTaxonomy[]>(`/m_taxonomy/list_full/`, {params})
    }

    listMatTaxonomyMapping(src_mat_taxonomy: number, dst_mat_taxonomy: number): Promise<AxiosResponse<m_taxonomy.MaterializedCategoryMap[]>> {
        if (environment.isDemo) return mithra_http.mockApi.get('/sales_demo/suggestor/category_map.json');
        return this.http.get<m_taxonomy.MaterializedCategoryMap[]>(`/m_taxonomy/category_map/`, {
            params: {src_mat_taxonomy, dst_mat_taxonomy}
        })
    }

    createMatTaxonomyMapping(create: m_taxonomy.CreateMaterializedCategoryMap): Promise<AxiosResponse<m_taxonomy.MaterializedCategoryMap[]>> {
        return this.http.post(`/m_taxonomy/category_map/`, create);
    }

    createMatTaxonomyMapping2(create: m_taxonomy.CreateMaterializedCategoryMap2): Promise<AxiosResponse<m_taxonomy.MaterializedCategoryMap[]>> {
        return this.http.post(`/m_taxonomy/category_map/create_raw/`, create);
    }

    deleteMatTaxonomyMapping(id: number): Promise<AxiosResponse<void>> {
        return this.http.delete(`/m_taxonomy/category_map/${id}/`);
    }

    downloadTaxonomyExcel(taxonomyId: number, filename: string) {
        return this.http.post(`/taxonomy/${taxonomyId}/download_as_excel/`, {filename}, {responseType: 'blob'})
    }

    applyTaxonomyMappingAi(data: ApplyTaxonomyMappingRequestData): Promise<AxiosResponse<{dst_databag: number}>> {
        return this.http.post(`/m_taxonomy/category_map/apply/apply_taxonomy_mapping_ai/`, data)
    }

    debugDeleteAiClassification(databag: number): Promise<AxiosResponse> {
        return this.http.post(`/m_taxonomy/category_map/apply/delete_ai_result/`, {databag})
    }

    debugDeleteReviewMaterialization(databag: number): Promise<AxiosResponse> {
        return this.http.post(`/materialize_manager/delete_review_materialization/`, {databag})
    }

    downloadReviewExcel(databag: number, filename: string): Promise<AxiosResponse> {
        return this.http.post(`/m_review/export/download_excel/`, {databag, filename}, {responseType: 'blob'})
    }

    downloadReviewCsv(bagId: number, fileName: string) {
        return this.http.post(`/m_review/export/download_csv_zip/`, {
            databag: bagId, filename: fileName
        }, {responseType: 'blob'})
    }

    listPpvGroup(filter: PpvGroupFilter): PagePromise<PpvGroup> {
        return this.http.get<PageResponse<PpvGroup>>(`/m_koi/ppv_group/`, {
            params: filter,
            paramsSerializer: {indexes: null}
        })
    }

    listUnfilteredPpvGroup(databag: number, p_name: string): Promise<AxiosResponse<[PpvGroup] | []>> {
        const filter = {databag: databag, p_name: p_name, no_page: true};
        return this.http.get(`/m_koi/ppv_group/`, {params: filter, paramsSerializer: {indexes: null}})
    }

    fetchPpvGroupByDescription(databag: number, p_description: string): Promise<AxiosResponse<[PpvGroup] | []>> {
        const filter = {databag: databag, p_description: p_description, no_page: true};
        return this.http.get(`/m_koi/ppv_group/`, {params: filter, paramsSerializer: {indexes: null}})
    }

    listPpvOpportunity(filter: PpvOpportunityListFilter): PagePromise<PpvOpportunity> {
        return this.http.get<PageResponse<PpvOpportunity>>(`/m_koi/ppv_opportunity/`, {
            params: filter,
            paramsSerializer: {indexes: null}
        })
    }

    retrievePpvOpportunity(opportunityId: number) {
        return this.http.get<PpvOpportunity>(`/m_koi/ppv_opportunity/${opportunityId}/`)
    }

    retrievePpvStatsPerSupplier(filter: PpvItemListFilter) {
        return this.http.get<PpvStatsPerSupplier[]>(`/m_koi/ppv_statistics/ppv_stats_per_supplier/`, {
            params: filter,
            paramsSerializer: {indexes: null}
        })
    }

    retrievePpvStatsPerPart(filter: PpvItemListFilter) {
        return this.http.get<PpvStatsPerPart[]>(`/m_koi/ppv_statistics/ppv_stats_per_supplier_part/`, {
            params: filter,
            paramsSerializer: {indexes: null}
        })
    }

    retrievePpvStatsPerQuarter(filter: PpvItemListFilter) {
        return this.http.get<PpvStatsPerQuarter[]>(`/m_koi/ppv_statistics/ppv_spend_over_time/`, {
            params: filter,
            paramsSerializer: {indexes: null}
        })
    }

    updatePpvOpportunity(ppv_opportunity_id: number, data: PpvOpportunity_PATCH): Promise<AxiosResponse<PpvGroup>> {
        return this.http.patch(`/m_koi/ppv_opportunity/${ppv_opportunity_id}/`, data)
    }

    createPpvOpportunity(data: CreatePpvOpportunity): Promise<AxiosResponse<PpvOpportunity>> {
        return this.http.post(`/m_koi/ppv_opportunity/`, data)
    }

    listPpvParts(filter: PpvItemListFilter): Promise<AxiosResponse<PpvPart[]>> {
        return this.http.get<PpvPart[]>(`/m_koi/ppv_item/`, {params: filter, paramsSerializer: {indexes: null}})
    }

    /**
     * Deletes all items with this filter
     */
    deletePpvItems(filter: OnlyRequired<PpvItemDeletionFilter, 'databag'>): Promise<AxiosResponse<void>> {
        return this.http.delete<void>(`/m_koi/ppv_item/`, {params: filter, paramsSerializer: {indexes: null}})
    }

    downloadPpvTable(data: DownloadTableRequest) {
        return this.http.post(`/m_koi/ppv_download/`, data, {responseType: 'blob'})
    }

    listParentSupplierReviewRows(filters: string[][]): Promise<AxiosResponse<PageResponse<ParentSupplierReviewRow>>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/m_sp_review/parent_rows/`, {params})
    }

    updateParentSupplierReviewRow(data: UpdateParentSupplierReviewRowData): Promise<AxiosResponse<ParentSupplierReviewRow>> {
        return this.http.patch(`/m_sp_review/parent_rows/` + data.prev_m_parent_supplier_id.toString() + `/`,
            {'sp_id': data.sp_id, 'sp_name': data.sp_name})
    }

    deleteParentSupplierReviewRow(id: number) {
        return this.http.delete(`/m_sp_review/parent_rows/` + id.toString() + `/`)
    }

    listSuggestedParentSupplierReviewRows(filters: string[][]): Promise<AxiosResponse<PageResponse<ParentSupplierChoice>>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/m_sp_review/suggested_parent_supplier/`, {params})
    }

    listSupplierReviewRows(filters: string[][]): Promise<AxiosResponse<PageResponse<SupplierReviewRow>>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/m_sp_review/supplier_rows/`, {params})
    }

    updateSupplierReviewRow(data: UpdateSupplierData): Promise<AxiosResponse<never>> {
        return this.http.patch<never>(`/m_sp_review/supplier_rows/` + data.id.toString() + `/`, {
            parent_supplier_id: data.parent_supplier_id,
            sp_id: data.sp_id,
            sp_name: data.sp_name
        })
    }

    deleteSupplierReviewRow(id: number) {
        return this.http.delete(`/m_sp_review/supplier_rows/` + id.toString() + `/`)
    }

    listCommonSupplier(filters: string[][]): Promise<AxiosResponse<PageResponse<CommonParentSupplier>>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/synergy/common_parent_suppliers/`, {params})
    }

    storeSupplierReviewUpdate(
        supplierRowId: number,
        parent_supplier: number | undefined,
        sp_id: number | undefined,
        sp_name: string,
    ): Promise<AxiosResponse<unknown>> {
        const data: any = {sp_name}
        if (sp_id) data['sp_id'] = sp_id
        if (parent_supplier) data['parent_supplier'] = parent_supplier
        return this.http.patch(`/m_sp_review/supplier_rows/${supplierRowId}/store_review/`, data)
    }

    getMatSupplierReviewStatistics(databag_id: number): Promise<AxiosResponse<MatSupplierReviewStatistics>> {
        return this.http.get<MatSupplierReviewStatistics>(`/m_sp_review/statistics/${databag_id}/`)
    }

    updateMatSupplierReviewStatistics(databag_id: number): Promise<AxiosResponse<MatSupplierReviewStatistics>> {
        return this.http.patch<MatSupplierReviewStatistics>(`/m_sp_review/statistics/${databag_id}/`)
    }

    getTaxonomySuggestionsV2(taxonomy_suggestion_v2: number): Promise<TaxonomySuggestionSerializer<ApiSuggestionTreeResponse<taxonomy_suggester_v2.APIValues>>> {
        // TODO: [CAT-1346] For demo purposes we use the dpw_demo data
        console.log("taxonomy suggestion avaiable!")
        const baseURL = window.location.origin;
        return this.http.get('taxonomy-suggestion/taxonomy_suggestion.json', {baseURL}).then((r) => {
            return r.data;
        });

        // return this.http.get(`/taxonomy_suggestion/${taxonomy_suggestion_v2}/`).then((r) => {
        //     return r.data;
        // });
    }

    getTaxonomySuggestions(taxonomy_suggestion: number): Promise<TaxonomySuggestionSerializer<ApiSuggestionTreeResponse<taxonomy_health_check.APIValues>>> {
        // TODO: [CAT-1346] For demo purposes we use the dpw_demo data
        // const baseURL = window.location.origin;
        // return this.http.get('dpw_demo/suggestor_example.json', {baseURL}).then((r) => {
        //     return r.data;
        // });

        return this.http.get(`/taxonomy_suggestion/${taxonomy_suggestion}/`).then((r) => {
            return r.data;
        });
    }

    getTaxonomyHealthChecks(taxonomy: number): Promise<TaxonomyHealthCheckSerializer<ApiSuggestionTreeResponse<m_taxonomy.Data>>[]> {
        if (environment.isDemo) {
            // TODO: Verify that this data works with the new THC components
            return mithra_http.mockApi.get('sales_demo/suggestor/health_check.json')
        }
        return this.http.get(`/taxonomy_health_checks/?taxonomy_id=${taxonomy}`).then((r) => {
            return r.data;
        });
    }

    putTaxonomyHealthCheck(taxonomyHealthCheckId: number, data: taxonomy_health_check.HealthCheckApiResponse): Promise<AxiosResponse<TaxonomyHealthCheckSerializer<ApiSuggestionTreeResponse<m_taxonomy.Data>>>> {
        return this.http.put(`/taxonomy_health_checks/${taxonomyHealthCheckId}/`, data);
    }

    applyTaxonomyHealthChecks(taxonomyHealthCheckId: number): Promise<AxiosResponse<void>> {
        return this.http.post(`/taxonomy_health_checks/${taxonomyHealthCheckId}/apply/`);
    }

    createNewTaxonomyHealthCheck(taxonomy: number): Promise<AxiosResponse<void>> {
        return this.http.post(`/taxonomy_health_checks/`, {taxonomy});
    }

    putTaxonomySuggestions(taxonomy_suggestion: number, data: ApiUpdateSuggestionTreeResponse<m_taxonomy.Data>): Promise<AxiosResponse<ApiSuggestionTreeResponse<m_taxonomy.Data>>> {

        const suggestion_state = {
            'suggestion_state': data
        }

        return this.http.put(`/taxonomy_suggestion/${taxonomy_suggestion}/`, suggestion_state);
    }

    saveTaxonomySuggestions(taxonomy_suggestion: number): Promise<AxiosResponse<ApiSuggestionTreeResponse<m_taxonomy.Data>>> {
        return this.http.post(`taxonomy_suggestion/${taxonomy_suggestion}/apply_suggestion/`, {});
    }

    updateTaxonomySuggestionValues(taxonomy_suggestion_id: number): Promise<any> {
        return this.http.post(`/taxonomy_suggestion/${taxonomy_suggestion_id}/update_values/`);
    }

    latestSupplierParentIdentificationJob(databag_id: number): Promise<AxiosResponse<AiSupplierParentIdentificationJobStatusSerializer>> {
        return this.http.get(`/ai_io/sp_identification/latest/`, {params: {databags: databag_id}})
    }

    createSupplierParentIdentificationJob(databag_id: number): Promise<AxiosResponse<{id: number}>> {
        return this.http.post('/ai_io/sp_identification/', {databags: [databag_id]})
    }

    createAiJob(data: AiCategorizationJobCreateSerializer): Promise<AxiosResponse<{id: number}>> {
        return this.http.post('/ai_io/', data)
    }

    getApprovalStatus(databag: number): Promise<AxiosResponse<CategorizationStatus>> {
        // TODO CAT-2549: Consolidate polling-endpoints and migrate to PubSub topic
        return this.http.get(`/approval/databag-status/${databag}/`)
    }

    getCategorizationVersionedStatistics(databag: number): Promise<AxiosResponse<CategorizationVersionedStatisticsSerializer>> {
        const params = new URLSearchParams()
        this.applyRevisionHack(params);
        return this.http.get(`/m_review/stats/versioned/${databag}/`, {params})
    }

    recalculateCategorizationVersionedStatistics(databag: number): Promise<AxiosResponse<CategorizationVersionedStatisticsSerializer>> {
        const params = new URLSearchParams()
        return this.http.get(`/m_review/stats/versioned/${databag}/recalculate/`, {params})
    }

    getMatReviewSampleStatistics(databag: number): Promise<AxiosResponse<MatReviewSampleStatistic>> {
        return this.http.get(`/m_review/sample-stats/${databag}/`)
    }

    getAdvancedSpendTree(databag_id: number, filters: HttpQueryParams): Promise<AxiosResponse<AdvancedTreeData[]>> {
        return this.http.get<AdvancedTreeData[]>(`/insights/spend_data/${databag_id}/tree_data/`, {params: filters});
    }

    getAdvancedSpendCountry(databag_id: number, filters: HttpQueryParams): Promise<AxiosResponse<AdvancedGeoData[]>> {
        return this.http.get<AdvancedGeoData[]>(`/insights/spend_data/${databag_id}/country_data/`, {params: filters});
    }

    getAdvancedSpendTimePerSupplier(databag_id: number, filters: HttpQueryParams): Promise<AxiosResponse<AdvancedSpendPerGroup[]>> {
        return this.http.get<AdvancedSpendPerGroup[]>(`/insights/spend_data/${databag_id}/line_data_per_supplier/`, {params: filters});
    }

    getAdvancedSpendTimePerCategory(databag_id: number, filters: HttpQueryParams, category: string): Promise<AxiosResponse<AdvancedSpendPerGroup[]>> {
        return this.http.get<AdvancedSpendPerGroup[]>(`/insights/spend_data/${databag_id}/line_data_per_category/?active=${category}`, {params: filters});
    }

    getAdvancedSpendKPI(databag_id: number, filters: HttpQueryParams): Promise<AxiosResponse<[AdvancedSpendKPI]>> {
        return this.http.get<[AdvancedSpendKPI]>(`/insights/spend_data/${databag_id}/kpi/`, {params: filters});
    }

    getAdvancedDonutData(databag_id: number, filters: HttpQueryParams): Promise<AxiosResponse<DonutChartSupplierSpendData[]>> {
        return this.http.get<AdvancedSpendData[]>(`/insights/spend_data/${databag_id}/donut_data/`, {params: filters});
    }

    getAdvancedSpendConcentrationData(databag_id: number, filters: HttpQueryParams): Promise<AxiosResponse<AdvancedSpendData[]>> {
        return this.http.get<AdvancedSpendData[]>(`/insights/spend_data/${databag_id}/spend_concentration_data/`, {params: filters});
    }

    getAdvancedOpportunityData(
        databag_id: number, filters: HttpQueryParams, ordering: string
    ): Promise<AxiosResponse<AdvancedDashboardPageResponse<AdvancedOpportunityData>>> {
        const params = {...filters}
        if (ordering) params['ordering'] = ordering;
        // noinspection TypeScriptValidateTypes
        return this.http.get<AdvancedDashboardPageResponse<AdvancedOpportunityData>>(
            `/insights/spend_data/${databag_id}/opportunity_data/`, {params: params}
        ).then(transformAdvancedDashboardTableResponse);
    }

    getAdvancedSupplierBreakdownData(
        databag_id: number, filters: HttpQueryParams, ordering: string
    ): Promise<AxiosResponse<AdvancedDashboardPageResponse<AdvancedSupplierBreakdownData>>> {
        const params = {...filters}
        if (ordering) params['ordering'] = ordering;
        // noinspection TypeScriptValidateTypes
        return this.http.get<AdvancedDashboardPageResponse<AdvancedSupplierBreakdownData>>(
            `/insights/spend_data/${databag_id}/supplier_breakdown_data/`, {params: params}
        ).then(transformAdvancedDashboardTableResponse);
    }

    downloadInsightsExcel(databag_id: number, filename: string, columns: ExcelDataColumns[], filters: HttpQueryParams, ordering: string, url: string): Promise<AxiosResponse> {
        const params = {...filters};
        if (ordering) params['ordering'] = ordering;
        params['filename'] = filename;
        params['columns'] = JSON.stringify(columns);
        params['url'] = url;
        params['extra_aggregates'] = JSON.stringify([
            {"field": "transactions", "func": "SUM", "name": "Totals"},
        ]);
        const p = toParams(toJS(params))
        return this.http.post<AdvancedSupplierBreakdownData[]>(`/insights/spend_data/${databag_id}/supplier_breakdown_data/?${p}`, {}, {responseType: 'blob'})
    }

    getAdvancedBusinessUnitPivot(
        databag_id: number, filters: HttpQueryParams, ordering: string
    ): Promise<AxiosResponse<AdvancedBusinessUnitPivotData>> {
        const params = {...filters}
        if (ordering) params['ordering'] = ordering;
        // noinspection TypeScriptValidateTypes
        return this.http.get<AdvancedBusinessUnitPivotData>(
            `/insights/spend_data/${databag_id}/business_unit_pivot/`, {params: params}
        ).then(resp => transformAdvancedDashboardTableResponse(resp) as AxiosResponse<AdvancedBusinessUnitPivotData>);
    }

    downloadAdvancedBusinessUnitPivot(databag_id: number, filename: string, columns: ExcelDataColumns[], filters: HttpQueryParams, ordering: string, url: string): Promise<AxiosResponse> {
        const params = {...filters};
        if (ordering) params['ordering'] = ordering;
        params['filename'] = filename;
        params['columns'] = JSON.stringify(columns);
        params['url'] = url;
        params['extra_aggregates'] = JSON.stringify([
            {"field": "transactions", "func": "SUM", "name": "Totals"},
        ]);
        const p = toParams(toJS(params))
        return this.http.post<AxiosResponse>(`/insights/spend_data/${databag_id}/business_unit_pivot/?${p}`, {}, {responseType: 'blob'})
    }

    getInsightsJobRunning(): Promise<AxiosResponse<InsightsJobRunning>> {
        // TODO CAT-2549: Consolidate polling-endpoints and migrate to PubSub topic
        return this.http.get<InsightsJobRunning>(`/insights/job/recalculation_running/`)
    }

    createInsightsJon(): Promise<AxiosResponse<number>> {
        return this.http.post(`/insights/job/recalculate/`, {})
    }

    searchInsightGeoRegions(databag_id: number, filters: HttpQueryParams, search: string): Promise<AxiosResponse<Array<Nullable<GeocodedRegion> & TotalSpend>>> {
        const params = {
            ...filters,
            search: search ? search : undefined, // Avoid sending empty search
        };
        return this.http.get(`/insights/spend_data/${databag_id}/geo_region_data/`, {params})
    }

    searchInsightCDescriptions(databag_id: number, filters: HttpQueryParams, search: string): Promise<AxiosResponse<string[]>> {
        const params = {
            ...filters,
            search: search ? search : undefined, // Avoid sending empty search
        };
        return this.http.get(`/insights/spend_data/${databag_id}/c_description_list/`, {params})
    }

    searchInsightPlants(databag_id: number, filters: HttpQueryParams, search: string): Promise<AxiosResponse<string[]>> {
        const params = {
            ...filters,
            search: search ? search : undefined, // Avoid sending empty search
        };
        return this.http.get(`/insights/spend_data/${databag_id}/plant_list/`, {params})
    }

    searchInsightCAddress(databag_id: number, filters: HttpQueryParams, search: string): Promise<AxiosResponse<string[]>> {
        const params = {
            ...filters,
            search: search ? search : undefined, // Avoid sending empty search
        };
        return this.http.get(`/insights/spend_data/${databag_id}/c_address_list/`, {params})
    }

    searchInsightSupplier(databag_id: number, filters: HttpQueryParams, search: string): Promise<AxiosResponse<Array<Supplier & TotalSpend>>> {
        const params = {
            ...filters,
            search: search ? search : undefined, // Avoid sending empty search
        };
        return this.http.get(`/insights/spend_data/${databag_id}/supplier_list/`, {params});
    }

    searchInsightParentSupplier(databag_id: number, filters: HttpQueryParams, search: string): Promise<AxiosResponse<Array<ParentSupplier & TotalSpend>>> {
        const params = {
            ...filters,
            search: search ? search : undefined, // Avoid sending empty search
        };
        return this.http.get(`/insights/spend_data/${databag_id}/parent_supplier_list/`, {params})
    }

    searchInsightBusinessUnits(databag_id: number, filters: HttpQueryParams, search: string): Promise<AxiosResponse<Array<BusinessUnit & TotalSpend>>> {
        const params = {
            ...filters,
            search: search ? search : undefined, // Avoid sending empty search
        };
        return this.http.get(`/insights/spend_data/${databag_id}/business_unit_list/`, {params})
    }

    searchInsightL1(databag_id: number, filters: HttpQueryParams, search: string): Promise<AxiosResponse<Array<[string, number]>>> {
        const params = {
            ...filters,
            search: search ? search : undefined, // Avoid sending empty search
        };
        return this.http.get(`/insights/spend_data/${databag_id}/category_l1_list/`, {params})
    }

    // searchParentSupplier(databag_id: number, search: string): PagePromise<ParentSupplierSearchResults> {
    //     return this.http.get(`/data/parent_supplier/`, {params: {search, databag_id}});
    // }

    private applyRevisionHack(params: URLSearchParams) {
        if (this.revisionHack !== undefined) {
            if (this.revisionHack === 'customer') {
                params.set('revision_id', '0');
            } else {
                params.set('revision_id', String(this.revisionHack));
            }
        }
    }

    putChatMessage(data: {message: string}) {
        return this.http.put<MessageElement[]>('/chat/chat/', data)
    }

    getSrcDatabagIds(databagId: number) {
        return this.http.get<number[]>(`synergy-databag/${databagId}/src_databag_ids/`);
    }

    getDatabagConfiguration(databagId: number) {
        return this.http.get<DatabagConfiguration>(`config/databag/${databagId}/`);
    }

    getCustomerConfiguration() {
        return this.http.get<CustomerConfiguration[]>(`config/customer/`);
    }

    listGoldenRecordClusters(databagId: number, filters: string[][]): Promise<AxiosResponse<PageResponse<GoldenRecordCluster>>> {
        const params = new URLSearchParams(filters);
        return this.http.get<PageResponse<GoldenRecordCluster>>(`golden-record-cluster/${databagId}/`, {params});
    }

    updateGoldenRecordClusters(databagId: number, id: number, limitedAccessEnabled: boolean): Promise<AxiosResponse<GoldenRecordCluster>> {
        return this.http.patch<GoldenRecordCluster>(`golden-record-cluster/${databagId}/${id}/`, {limited_access_enabled: limitedAccessEnabled});
    }

    listGoldenRecordPropertyType(clusterId: number, filters: string[][]): Promise<AxiosResponse<PageResponse<GoldenRecordClusterPropertyType>>> {
        const params = new URLSearchParams(filters);
        return this.http.get<PageResponse<GoldenRecordClusterPropertyType>>(`golden-record-property-type/${clusterId}/`, {params});
    }

    updateGoldenRecordPropertyType(clusterId: number, id: number, selected: boolean): Promise<AxiosResponse<GoldenRecordClusterPropertyType>> {
        return this.http.patch<GoldenRecordClusterPropertyType>(`golden-record-property-type/${clusterId}/${id}/`, {selected});
    }

    updateBatchGoldenRecordPropertyType(clusterId: number, selected_ids: number[], not_selected_ids: number[]): Promise<AxiosResponse<never>> {
        return this.http.patch(`golden-record-property-type/${clusterId}/batch-save/`, {
            selected_ids,
            not_selected_ids
        });
    }
}
